import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { ChevronRight, Calendar, MessageCircle, FileText, SmilePlus, Smile, Meh, Frown, Angry, Plus, Users } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const MOOD_CONFIG = {
  5: { label: 'Très bien', color: '#4A9B8F', bg: '#E0F2F0', Icon: SmilePlus },
  4: { label: 'Bien', color: '#68B9B0', bg: '#D4EDEA', Icon: Smile },
  3: { label: 'Moyen', color: '#E8A838', bg: '#FEF3C7', Icon: Meh },
  2: { label: 'Difficile', color: '#E8967A', bg: '#FEE2D4', Icon: Frown },
  1: { label: 'Très difficile', color: '#DC6B6B', bg: '#FEE2E2', Icon: Angry },
};

const formatRelative = (dt) => {
  if (!dt) return '';
  try {
    const d = new Date(dt);
    const now = new Date();
    const diff = now - d;
    if (diff < 3600000) return `il y a ${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return `il y a ${Math.floor(diff / 3600000)}h`;
    if (diff < 604800000) return `il y a ${Math.floor(diff / 86400000)} j`;
    return format(d, 'd MMM', { locale: fr });
  } catch { return ''; }
};

export default function PatientDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [patient, setPatient] = useState(null);
  const [todayMood, setTodayMood] = useState(null);
  const [nextApt, setNextApt] = useState(null);
  const [recentConversations, setRecentConversations] = useState([]);

  useEffect(() => {
    if (user) loadData();
  }, [user]);

  const loadData = async () => {
    try {
      // Profil patient
      const { data: patientData } = await supabase
        .from('patients')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();
      setPatient(patientData);

      // Prochain RDV
      const { data: apts } = await supabase
        .from('appointments')
        .select('*')
        .eq('patient_id', user.id)
        .gte('start_datetime', new Date().toISOString())
        .order('start_datetime')
        .limit(1);
      setNextApt(apts?.[0] || null);

      // Mood aujourd'hui
      const today = new Date().toISOString().split('T')[0];
      const { data: moodDataArr } = await supabase
        .from('patient_moods')
        .select('*')
        .eq('patient_id', user.id)
        .eq('date', today);
      const moodMatin = moodDataArr?.find(m => m.mood_type === 'matin') || null;
      const moodSoir = moodDataArr?.find(m => m.mood_type === 'soir') || null;
      setTodayMood({ matin: moodMatin, soir: moodSoir });

      // Conversations récentes
      const { data: convs } = await supabase
        .from('conversations')
        .select('*')
        .eq('patient_id', user.id)
        .order('last_message_at', { ascending: false })
        .limit(2);
      setRecentConversations(convs || []);

    } catch (error) {
      console.error('Error loading patient dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const todayFr = format(new Date(), "EEEE d MMMM", { locale: fr });
  const moodMatinCfg = todayMood?.matin ? MOOD_CONFIG[todayMood.matin.mood_level] : null;
  const moodSoirCfg = todayMood?.soir ? MOOD_CONFIG[todayMood.soir.mood_level] : null;

  if (loading) return (
    <div className="p-6 space-y-4">
      {[1,2,3,4].map(i => <div key={i} className="h-24 bg-stone-100 rounded-3xl animate-pulse" />)}
    </div>
  );

  return (
    <div className="px-5 pt-5 pb-4 space-y-5">

      {/* Greeting */}
      <div>
        <p className="text-xs text-slate-400 font-body capitalize">{todayFr}</p>
        <h1 className="text-2xl font-heading font-bold text-slate-800 mt-0.5">
          Bonjour, {patient?.first_name || 'vous'} 👋
        </h1>
      </div>

      {/* Carte profil patient */}
      <div
        onClick={() => navigate('/patient/profil')}
        className="passerelle-card cursor-pointer"
        style={{ background: 'linear-gradient(135deg, #7C6BAE 0%, #5B4D8A 100%)' }}
      >
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center text-white font-heading font-bold text-2xl flex-shrink-0">
            {patient?.first_name?.[0] || '?'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white/70 text-xs font-body mb-0.5">Mon dossier</p>
            <h2 className="text-white font-heading font-bold text-lg leading-tight">
              {patient?.first_name} {patient?.last_name}
            </h2>
            {patient?.diagnosis && (
              <p className="text-white/80 text-sm font-body">{patient.diagnosis}</p>
            )}
          </div>
          <div className="flex items-center gap-1 text-white/80">
            <span className="text-xs font-heading font-semibold">Voir</span>
            <ChevronRight size={16} />
          </div>
        </div>
      </div>

      {/* Mood + Prochain RDV */}
      <div className="grid grid-cols-2 gap-3">
        {/* Mood */}
        <div className="passerelle-card cursor-pointer">
          <p className="section-label mb-2">Mood du jour</p>
          <div className="flex gap-2">
            <div className="flex-1 flex flex-col items-center gap-1 p-2 rounded-xl" style={{ backgroundColor: moodMatinCfg ? moodMatinCfg.bg : '#F5F5F4' }}>
              <span className="text-[10px] text-slate-400 font-heading font-semibold">☀️ Matin</span>
              {moodMatinCfg ? (
                <>
                  <span className="text-2xl">{['😢','😟','😐','🙂','😄'][todayMood.matin.mood_level - 1]}</span>
                  <span className="text-[10px] font-heading font-semibold text-center leading-tight" style={{ color: moodMatinCfg.color }}>{moodMatinCfg.label}</span>
                </>
              ) : (
                <div className="flex flex-col items-center gap-1">
                  <Plus size={16} className="text-slate-300" />
                  <span className="text-[10px] text-slate-400 font-body text-center">Non renseigné</span>
                </div>
              )}
            </div>
            <div className="flex-1 flex flex-col items-center gap-1 p-2 rounded-xl" style={{ backgroundColor: moodSoirCfg ? moodSoirCfg.bg : '#F5F5F4' }}>
              <span className="text-[10px] text-slate-400 font-heading font-semibold">🌙 Soir</span>
              {moodSoirCfg ? (
                <>
                  <span className="text-2xl">{['😢','😟','😐','🙂','😄'][todayMood.soir.mood_level - 1]}</span>
                  <span className="text-[10px] font-heading font-semibold text-center leading-tight" style={{ color: moodSoirCfg.color }}>{moodSoirCfg.label}</span>
                </>
              ) : (
                <div className="flex flex-col items-center gap-1">
                  <Plus size={16} className="text-slate-300" />
                  <span className="text-[10px] text-slate-400 font-body text-center">Non renseigné</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Prochain RDV */}
        <div
          onClick={() => navigate('/patient/planning')}
          className="passerelle-card cursor-pointer"
        >
          <p className="section-label mb-2">Prochain RDV</p>
          {nextApt ? (
            <div>
              <div className="w-10 h-10 rounded-xl bg-sage-100 flex items-center justify-center mb-2">
                <Calendar size={18} className="text-sage-600" />
              </div>
              <p className="font-heading font-semibold text-sm text-slate-700 leading-tight">{nextApt.title}</p>
              <p className="text-xs text-slate-400 font-body mt-1">
                {format(new Date(nextApt.start_datetime), "EEE d MMM 'à' HH'h'mm", { locale: fr })}
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 mt-2">
              <Calendar size={22} className="text-slate-300" />
              <p className="text-xs text-slate-400 font-body text-center">Aucun RDV à venir</p>
            </div>
          )}
        </div>
      </div>

      {/* Actions rapides */}
      <div>
        <p className="section-label mb-3">Actions rapides</p>
        <div className="grid grid-cols-4 gap-2">
          {[
            { icon: Calendar, label: 'Planning', path: '/patient/planning', color: '#6B9FD4' },
            { icon: MessageCircle, label: 'Messages', path: '/patient/messages', color: '#4A9B8F' },
            { icon: FileText, label: 'Documents', path: '/patient/documents', color: '#A78BCA' },
            { icon: Users, label: 'Mon équipe', path: '/patient/professionnels', color: '#E8967A' },
          ].map(({ icon: Icon, label, path, color }) => (
            <button
              key={path}
              onClick={() => navigate(path)}
              className="flex flex-col items-center gap-2 p-3 bg-white rounded-2xl shadow-card"
            >
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
                <Icon size={18} style={{ color }} />
              </div>
              <span className="text-[11px] font-heading font-semibold text-slate-600">{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Messages récents */}
      {recentConversations.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="section-label">Messages récents</p>
            <button onClick={() => navigate('/patient/messages')} className="text-sage-600 font-heading font-semibold text-xs flex items-center gap-1">
              Voir tout <ChevronRight size={14} />
            </button>
          </div>
          <div className="space-y-2">
            {recentConversations.map(conv => (
              <div
                key={conv.id}
                onClick={() => navigate(`/patient/messages/${conv.id}`)}
                className="passerelle-card cursor-pointer flex items-center gap-3 !p-4"
              >
                <div className="w-10 h-10 rounded-2xl bg-sage-100 flex items-center justify-center flex-shrink-0">
                  <MessageCircle size={18} className="text-sage-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-heading font-semibold text-sm text-slate-700 truncate">
                    {conv.professional_name || 'Professionnel'}
                  </p>
                  <p className="text-xs text-slate-400 font-body">{formatRelative(conv.last_message_at)}</p>
                </div>
                <ChevronRight size={16} className="text-slate-300" />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="h-4" />
    </div>
  );
}
